import {Inject, Injectable, OnDestroy} from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {
    AccountInfo, AuthenticationResult, AuthError, BrowserAuthError, EventMessage, EventType,
    InteractionRequiredAuthError, InteractionStatus, PopupRequest, RedirectRequest
} from '@azure/msal-browser';
import {filter, lastValueFrom, Subject, takeUntil} from 'rxjs';
import jwt_decode from 'jwt-decode';

/** Maximale Anzahl an Redirect-Versuchen bevor der Cache geleert und ein letzter Versuch gestartet wird */
const MAX_REDIRECT_ATTEMPTS = 3;
/** Zeitfenster in ms, in dem Redirect-Versuche gezählt werden */
const REDIRECT_WINDOW_MS = 60_000;
/** SessionStorage-Key für Redirect-Loop-Erkennung */
const REDIRECT_COUNTER_KEY = 'msal_redirect_attempts';
const REDIRECT_TIMESTAMP_KEY = 'msal_redirect_timestamp';
/** Flag das anzeigt, dass bereits ein "letzter Versuch nach Cache-Clear" läuft */
const REDIRECT_FINAL_ATTEMPT_KEY = 'msal_redirect_final_attempt';


@Injectable({
    providedIn: 'root'
})
// Service der die Authentifikation regelt
export class AuthenticationService implements OnDestroy
{
    private _error: boolean;
    private _bronze: boolean;
    private _silver: boolean;
    private _gold: boolean;
    private _platinum: boolean;
    private _admin: boolean;

    public UserId: string;

    public ActiveUser: AccountInfo;

    public HealthReset = false;
    public GatewayMonitoring = false;
    public Monitoring = false;

    isIframe = false;
    loginDisplay = false;
    private readonly _destroying$ = new Subject<void>();
    private readonly _loginComplete$ = new Subject<void>();
    private _loginPromise: Promise<string> | null = null;

    constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
                private msalService: MsalService,
                private msalBroadcastService: MsalBroadcastService)
    {
        this.setPackage('platinum');

    }

    /**
     * Prüft ob ein Redirect-Loop erkannt wird.
     * Zählt Redirect-Versuche innerhalb eines Zeitfensters.
     * Gibt 'ok' zurück wenn kein Loop, 'retry' wenn Cache geleert wurde und ein letzter Versuch erlaubt ist,
     * 'stop' wenn auch der letzte Versuch fehlgeschlagen ist.
     */
    private checkRedirectLoop(): 'ok' | 'retry' | 'stop'
    {
        try
        {
            // Prüfe ob wir bereits im "letzten Versuch" sind
            const finalAttempt = sessionStorage.getItem(REDIRECT_FINAL_ATTEMPT_KEY);
            if (finalAttempt === 'true')
            {
                // Der letzte Versuch nach Cache-Clear hat auch nicht funktioniert → endgültig stoppen
                console.error(
                    'Redirect-Loop: Auch nach Cache-Clear konnte kein Login durchgeführt werden. ' +
                    'Bitte Browser-Cache/Cookies manuell löschen oder anderen Browser verwenden.'
                );
                sessionStorage.removeItem(REDIRECT_FINAL_ATTEMPT_KEY);
                return 'stop';
            }

            const now = Date.now();
            const storedTimestamp = parseInt(sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY) || '0', 10);
            const storedCount = parseInt(sessionStorage.getItem(REDIRECT_COUNTER_KEY) || '0', 10);

            // Wenn das Zeitfenster abgelaufen ist, Zähler zurücksetzen
            if (now - storedTimestamp > REDIRECT_WINDOW_MS)
            {
                sessionStorage.setItem(REDIRECT_COUNTER_KEY, '1');
                sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, now.toString());
                return 'ok';
            }

            // Innerhalb des Zeitfensters: Zähler erhöhen
            const newCount = storedCount + 1;
            sessionStorage.setItem(REDIRECT_COUNTER_KEY, newCount.toString());

            if (newCount >= MAX_REDIRECT_ATTEMPTS)
            {
                console.warn(
                    `Redirect-Loop erkannt: ${newCount} Versuche in ${REDIRECT_WINDOW_MS / 1000}s. ` +
                    `Cache wird geleert und ein letzter Login-Versuch gestartet.`
                );
                return 'retry';
            }

            return 'ok';
        }
        catch (e)
        {
            // SessionStorage nicht verfügbar (z.B. private mode in manchen Browsern)
            return 'ok';
        }
    }

    /**
     * Setzt den Redirect-Zähler zurück (nach erfolgreichem Login).
     */
    private resetRedirectCounter(): void
    {
        try
        {
            sessionStorage.removeItem(REDIRECT_COUNTER_KEY);
            sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
            sessionStorage.removeItem(REDIRECT_FINAL_ATTEMPT_KEY);
        }
        catch (_) {}
    }

    /**
     * Führt einen sicheren Login-Redirect aus.
     * - Bei normalem Zustand: Redirect wird ausgeführt.
     * - Bei erkanntem Loop: Cache wird geleert und ein letzter sauberer Versuch gestartet.
     * - Wenn auch der letzte Versuch fehlschlägt: Redirect wird komplett gestoppt.
     * Gibt true zurück wenn ein Redirect ausgelöst wurde.
     */
    private async safeLoginRedirect(): Promise<boolean>
    {
        const loopState = this.checkRedirectLoop();

        if (loopState === 'stop')
        {
            console.error('Login-Redirect endgültig abgebrochen. Manuelles Eingreifen erforderlich.');
            return false;
        }

        if (loopState === 'retry')
        {
            // Cache leeren und Flag setzen dass dies der letzte Versuch ist
            console.warn('Redirect-Loop erkannt – Cache wird geleert und letzter Login-Versuch gestartet.');
            try { await this.msalService.instance.clearCache(); } catch (_) {}
            try
            {
                sessionStorage.setItem(REDIRECT_FINAL_ATTEMPT_KEY, 'true');
                // Zähler zurücksetzen damit der letzte Versuch nicht sofort wieder als Loop erkannt wird
                sessionStorage.removeItem(REDIRECT_COUNTER_KEY);
                sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
            }
            catch (_) {}
        }

        if (this.msalGuardConfig.authRequest)
        {
            await lastValueFrom(this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest));
        }
        else
        {
            await lastValueFrom(this.msalService.loginRedirect());
        }
        return true;
    }

    /**
     * Führt einen Login-Redirect aus ohne Loop-Check (für internen Gebrauch nach bereits erfolgter Prüfung).
     */
    private async doLoginRedirect(): Promise<void>
    {
        if (this.msalGuardConfig.authRequest)
        {
            await lastValueFrom(this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest));
        }
        else
        {
            await lastValueFrom(this.msalService.loginRedirect());
        }
    }

    /**
     * Prüft, ob ein aktiver Account gesetzt ist. Falls nicht und Accounts vorhanden sind,
     * wird der erste verfügbare Account als aktiver Account gesetzt.
     */
    checkAndSetActiveAccount()
    {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.msalService.instance.getActiveAccount();

        if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
            let accounts = this.msalService.instance.getAllAccounts();
            this.msalService.instance.setActiveAccount(accounts[0]);
        }
    }

    /**
     * Löst einen Login via Browser-Redirect aus.
     * Verwendet die konfigurierte authRequest falls vorhanden.
     */
    async loginRedirect()
    {
        if (this.msalGuardConfig.authRequest)
        {
            await lastValueFrom(this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest));
        }
        else
        {
            await lastValueFrom(this.msalService.loginRedirect());
        }
    }

    /**
     * Löst einen Login via Popup-Fenster aus.
     * Verwendet die konfigurierte authRequest falls vorhanden.
     */
    loginPopup()
    {
        if (this.msalGuardConfig.authRequest)
        {
            this.msalService.loginPopup({...this.msalGuardConfig.authRequest} as PopupRequest)
                .subscribe((response: AuthenticationResult) =>
                {
                    this.msalService.instance.setActiveAccount(response.account);
                });
        }
        else
        {
            this.msalService.loginPopup()
                .subscribe((response: AuthenticationResult) =>
                {
                    this.msalService.instance.setActiveAccount(response.account);
                });
        }
    }

    /**
     * Setzt das loginDisplay-Flag basierend darauf ob Accounts im MSAL-Cache vorhanden sind.
     */
    setLoginDisplay()
    {
        this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
    }

    /**
     * Holt ein Access-Token für die API. Versucht zunächst silent (aus Cache/Refresh),
     * bei Fehler wird je nach Fehlertyp ein Redirect oder Loop-Schutz ausgelöst.
     * @returns Das AuthenticationResult mit Access-Token oder null, falls ein Redirect ausgelöst wurde.
     */
    public async getToken(): Promise<AuthenticationResult>
    {
        const activeAccount = this.msalService.instance.getActiveAccount();
        const request = {
            scopes: ['api://97b96046-8098-48e4-8943-bf80d8391aa5/.default'],
            account: activeAccount
        };

        // Wenn kein aktiver Account vorhanden ist, kann kein Token silent geholt werden
        if (!activeAccount)
        {
            console.warn('getToken: Kein aktiver Account vorhanden – Login-Redirect wird ausgelöst.');
            await this.safeLoginRedirect();
            return null;
        }

        try
        {
            const result = await lastValueFrom(this.msalService.acquireTokenSilent(request));

            if (result == null)
            {
                console.log('token request is null');
                return result;
            }
            else
            {
                return result;
            }
        } catch (error)
        {
            console.log(error);

            // Wenn MSAL gerade eine Interaktion verarbeitet (z.B. MsalGuard Redirect),
            // nicht eingreifen – einfach warten bis die laufende Interaktion abgeschlossen ist.
            if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress')
            {
                console.log('Eine MSAL-Interaktion läuft bereits – kein zusätzlicher Redirect nötig.');
                return null;
            }

            const loopState = this.checkRedirectLoop();

            if (loopState === 'stop')
            {
                console.error('Token-Abruf fehlgeschlagen und Redirect-Loop nicht lösbar. Login wird gestoppt.');
                return null;
            }
            else if (loopState === 'retry')
            {
                // Loop erkannt – Cache leeren und letzten Versuch starten
                console.warn('Token-Abruf: Redirect-Loop erkannt – Cache wird geleert und letzter Login-Versuch gestartet.');
                try { await this.msalService.instance.clearCache(); } catch (_) {}
                try
                {
                    sessionStorage.setItem(REDIRECT_FINAL_ATTEMPT_KEY, 'true');
                    sessionStorage.removeItem(REDIRECT_COUNTER_KEY);
                    sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
                }
                catch (_) {}
                await this.doLoginRedirect();
            }
            else if (error instanceof InteractionRequiredAuthError)
            {
                // Keine gültige Session (z.B. privater Browser, abgelaufene Session)
                // → interaktiver Login via Redirect
                console.log('Interaction required – redirecting to login');
                await lastValueFrom(this.msalService.acquireTokenRedirect(request));
            }
            else
            {
                // Server-Fehler wie AADSTS1100001 / 1003005:
                // Korrupter oder abgelaufener Token im localStorage.
                // Cache leeren und komplett neu einloggen.
                console.log('Server error during token acquisition – clearing MSAL cache and forcing re-login', error);
                try
                {
                    await this.msalService.instance.clearCache();
                }
                catch (cacheError)
                {
                    console.warn('Could not clear MSAL cache', cacheError);
                }
                await this.safeLoginRedirect();
            }

            return null;
        }
    }

    /**
     * Meldet den Benutzer ab, entweder via Popup oder Redirect.
     * @param popup Wenn true, wird ein Popup zur Abmeldung verwendet, sonst ein Redirect.
     */
    public logout(popup?: boolean)
    {
        if (popup)
        {
            this.msalService.logoutPopup({
                mainWindowRedirectUri: '/'
            });
        }
        else
        {
            this.msalService.logoutRedirect();
        }
    }

    /**
     * Startet den gesamten Login-Flow. Wartet auf MSAL-Interaktionsstatus,
     * holt einen Token und setzt Benutzerrollen.
     * Gibt ein Promise zurück das nach erfolgreichem Login resolved.
     * Schützt gegen doppelte Aufrufe durch Caching des Promises.
     * @returns Promise das mit 'true' resolved, wenn Login erfolgreich war.
     */
    public login()
    {
        // Schutz gegen doppelte Aufrufe – gibt dasselbe Promise zurück
        if (this._loginPromise)
        {
            return this._loginPromise;
        }

        this._loginPromise = new Promise((resolve, reject) =>
        {
            this.isIframe = window !== window.parent && !window.opener;

            this.msalService.instance.enableAccountStorageEvents();

            // Wenn ein Account in einem anderen Tab hinzugefügt/entfernt wird
            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
                    takeUntil(this._destroying$)
                )
                .subscribe(() =>
                {
                    this.setLoginDisplay();
                });

            // Fehler-Handler: Bei AADSTS1100001 / 1003005 (Nonce/PKCE-Mismatch)
            // Cache bereinigen und neu einloggen
            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_FAILURE ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                    ),
                    takeUntil(this._destroying$)
                )
                .subscribe(async (msg: EventMessage) =>
                {
                    const error = msg.error as AuthError;
                    if (error && error.errorCode === 'invalid_request' &&
                        error.message && error.message.includes('1003005'))
                    {
                        console.warn('PKCE/Nonce-Mismatch erkannt (1003005) – MSAL-Cache wird geleert und Login neu gestartet.');
                        try { await this.msalService.instance.clearCache(); } catch (_) {}
                        await this.safeLoginRedirect();
                    }
                });

            // Haupt-Login-Flow: wartet bis MSAL keine Interaktion mehr laufen hat
            this.msalBroadcastService.inProgress$
                .pipe(
                    filter((status: InteractionStatus) => status === InteractionStatus.None),
                    takeUntil(this._loginComplete$)  // stoppt nach erfolgreichem Login
                )
                .subscribe(async () =>
                {
                    this.setLoginDisplay();
                    this.checkAndSetActiveAccount();

                    this.ActiveUser = this.msalService.instance.getActiveAccount();

                    if (!this.ActiveUser)
                    {
                        // Noch kein Account vorhanden – MSAL wird gleich einen Redirect auslösen
                        console.log('No active account yet – waiting for next interaction cycle');
                        return;
                    }

                    this.UserId = this.ActiveUser.localAccountId;

                    try
                    {
                        // Zustand VOR getToken() prüfen: Falls der Loop-Schutz bereits auf 'stop' steht,
                        // wird getToken() null zurückgeben OHNE einen Redirect auszulösen.
                        // In diesem Fall würde das Promise niemals resolven → App hängt komplett.
                        // Wir erkennen das vorab und rejecten das Promise explizit.
                        const preCheckLoopState = this.checkRedirectLoop();

                        const token = await this.getToken();

                        if (!token)
                        {
                            // getToken gab null zurück. Mögliche Gründe:
                            // 1. Redirect wurde ausgelöst → Seite wird neu geladen (Promise bleibt offen = OK)
                            // 2. interaction_in_progress → inProgress$ wird erneut 'None' emittieren, dann retry
                            // 3. Loop wurde endgültig gestoppt → hier müssen wir explizit rejecten,
                            //    da sonst kein weiterer inProgress$-Event kommt und ngOnInit() für immer wartet.
                            if (preCheckLoopState === 'stop')
                            {
                                const msg = 'Anmeldung fehlgeschlagen: Zu viele Login-Versuche ohne Erfolg. ' +
                                    'Bitte Browser-Cache und Cookies löschen oder einen anderen Browser verwenden.';
                                console.error(msg);
                                reject(new Error(msg));
                            }
                            // Fall 1 & 2: Redirect läuft oder Interaktion noch aktiv – einfach warten
                            return;
                        }

                        const decoded = jwt_decode(token.accessToken) as any;
                        this.setRoles(decoded.customRoles);
                        console.log('logged in');

                        // Login erfolgreich – Redirect-Zähler zurücksetzen
                        this.resetRedirectCounter();

                        // Subscription stoppen – Login ist abgeschlossen
                        this._loginComplete$.next();
                        this._loginComplete$.complete();

                        resolve('true');
                    }
                    catch (e)
                    {
                        console.error('Login failed during token acquisition', e);
                        reject(e);
                    }
                });

            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
                    takeUntil(this._destroying$)
                )
                .subscribe(() =>
                {
                    // Token erfolgreich erneuert
                });
        });

        return this._loginPromise;
    }

    /**
     * Erstellt HTTP-Headers mit Authorization-Bearer-Token für API-Aufrufe.
     * @returns HttpHeaders mit Content-Type, Accept und Authorization.
     * @throws Error wenn kein Access-Token verfügbar ist (z.B. Redirect läuft).
     */
    public async getDefaultHttpHeaders(): Promise<HttpHeaders>
    {
        const accessToken = await this.getToken();

        if (!accessToken)
        {
            // getToken hat einen Redirect ausgelöst – kein gültiger Token verfügbar
            throw new Error('No access token available – redirect in progress');
        }

        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': '*/*',
            'Authorization': 'Bearer ' + accessToken.accessToken
        });
    }

    /**
     * Cleanup bei Zerstörung des Services. Beendet alle aktiven Subscriptions.
     */
    ngOnDestroy()
    {
        this._destroying$.next(undefined);
        this._destroying$.complete();
        this._loginComplete$.next();
        this._loginComplete$.complete();
    }

    /**
     * Setzt die Benutzerrollen basierend auf dem customRoles-Array aus dem JWT-Token.
     * @param roles Array mit Rollennamen aus dem Access-Token.
     */
    private setRoles(roles: Array<string>)
    {
        if (roles == null)
        {
            return;
        }

        if (roles.includes('Health.ReadWrite.All'))
        {
            this.HealthReset = true;
        }

        if (roles.includes('Gateway.ReadWrite.All'))
        {
            this.GatewayMonitoring = true;
            this._admin = true;
        }

        if (roles.includes('Monitoring.ReadWrite.All'))
        {
            this.Monitoring = true;
        }
    }

    /*

        login() {

            this.msalService.loginPopup()
                .subscribe({
                    next: (result) => {
                        this.setLoginDisplay();
                        console.log(result);
                    },
                    error: (error) => console.log(error)
                });
        }

        setLoginDisplay() {
            this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
        }*/

    /**
     * Bekommt einen String und entscheidet daran welcher
     * Gruppe der User angehört
     * @param pPackage die Berechtigungsstufe des Users als string
     **/
    public setPackage(pPackage: string)
    {
        this.allFalse();

        switch (pPackage)
        {
            case 'bronze':
                this._bronze = true;
                break;
            case 'silver':
                this._bronze = true;
                this._silver = true;
                break;
            case 'gold':
                this._bronze = true;
                this._silver = true;
                this._gold = true;
                break;
            case 'platinum':
                this._bronze = true;
                this._silver = true;
                this._gold = true;
                this._platinum = true;
                break;
            case 'admin':
                this._bronze = true;
                this._silver = true;
                this._gold = true;
                this._platinum = true;
                this._admin = true;
                break;
            default:
                this.allFalse();
                this._error = true;
                break;
        }
    }

    /**
     * Gibt an ob User Bronze Berechtigungen hat
     */
    public isBronze(): boolean
    {
        return this._bronze;
    }

    /**
     * Gibt an ob User Silber Berechtigungen hat
     */
    public isSilver(): boolean
    {
        return this._silver;
    }

    /**
     * Gibt an ob User Gold Berechtigungen hat
     */
    public isGold(): boolean
    {
        return this._gold;
    }

    /**
     * Gibt an ob User Platin Berechtigungen hat
     */
    public isPlatinum(): boolean
    {
        return this._platinum;
    }

    /**
     * Gibt an ob der User Admin-Berechtigungen hat.
     */
    public isAdmin(): boolean
    {
        return this._admin;
    }

    /**
     * Setzt alle Berechtigungen auf false
     */
    private allFalse()
    {
        this._error = false;
        this._bronze = false;
        this._silver = false;
        this._gold = false;
        this._platinum = false;
        this._admin = false;
    }

    /**
     * Gibt eine lesbare Bezeichnung des Users wieder
     */
    public readableName(): string
    {
        const auth = JSON.parse(localStorage.getItem('auth'));
        if (auth['firstName'] && auth['lastName'])
        {
            return auth['firstName'] + ' ' + auth['lastName'];
        }
        else if (auth['loginName'])
        {
            return auth['loginName'];
        }
        else if (auth['email'])
        {
            return auth['email'];
        }
        else
        {
            return 'User';
        }


    }
}
